import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

export default function Pagination({
  totalPages,
  currentPage,
  setCurrentPage,
}) {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";

  if (totalPages <= 1) return null; // Hide if only one page

  return (
    <div
      className={`flex justify-center md:justify-end items-center mt-4 ${
        isRTL ? "space-x-reverse" : "space-x-1"
      }`}
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Previous Button */}
      <button
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className={`px-2 rounded transition ${
          currentPage === 1
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
        }`}
        aria-label={translate("common.pagination.previous")}
      >
        {isRTL ? <ChevronRight /> : <ChevronLeft />}
      </button>

      {/* Page Numbers with Dots */}
      {Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter((page) => {
          return (
            page <= 2 ||
            page > totalPages - 2 ||
            (page >= currentPage - 1 && page <= currentPage + 1)
          );
        })
        .map((page, idx, arr) => {
          const prevPage = arr[idx - 1];
          return (
            <React.Fragment key={page}>
              {prevPage && page - prevPage > 1 && (
                <span className='relative inline-flex items-center px-2 text-sm font-semibold text-gray-500'>
                  ...
                </span>
              )}
              <button
                onClick={() => setCurrentPage(page)}
                className={`px-3 rounded transition ${
                  page === currentPage
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {page}
              </button>
            </React.Fragment>
          );
        })}

      {/* Next Button */}
      <button
        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className={`px-2 rounded transition ${
          currentPage === totalPages
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
        }`}
        aria-label={translate("common.pagination.next")}
      >
        {isRTL ? <ChevronLeft /> : <ChevronRight />}
      </button>
    </div>
  );
}
